import { useState, useMemo, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { IBookingRequest } from '../types/Booking/booking';
import { localStorageHelper } from '../utils/localStorageHelper';
import { IAvailabilityByDate } from '../types';

export interface TimeOption {
    value: string;
    disabled: boolean;
}

interface UseBookingProps {
    professionalUid: string;
    availability?: IAvailabilityByDate[] | undefined;
    controlledDate?: Dayjs | null;
    controlledTime?: string;
    consultTime?: number;
    modality?: string | null;
    bookingMode?: string;
    requestSchedule?: { 
        type?: string, 
        days?: number[], 
        start?: string, 
        end?: string,
        schedule?: { [key: number]: { active: boolean; start: string; end: string } } 
    };
}

export const useBookingProfessional = ({
    professionalUid,
    availability,
    controlledDate,
    controlledTime,
    consultTime = 60,
    modality,
    bookingMode = 'direct',
    requestSchedule,
}: UseBookingProps) => {
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
    const [selectedTime, setSelectedTime] = useState<string>('');
    const [timeError, setTimeError] = useState<string>('');

    const [bookingModality, setBookingModality] = useState<string>('');
    const [modalityTooltipOpen, setModalityTooltipOpen] = useState(false);

    const isControlled = controlledDate !== undefined;
    const currentDate = isControlled ? controlledDate : selectedDate;
    const currentTime = isControlled ? controlledTime : selectedTime;

    useMemo(() => {
        if (modality === 'Presencial') setBookingModality('Presencial');
        else if (modality === 'Virtual') setBookingModality('Virtual');
        else setBookingModality('');
    }, [modality]);


    const availabilityByDateMap = useMemo(() => {
        const map = new Map<string, { start: string; end: string }[]>();
        if (availability) {
            availability.forEach((day) => {
                const key = day.date;
                const slots = day.slots?.map(s => ({ start: s.start, end: s.end })) ?? [];
                const existing = map.get(key) || [];
                map.set(key, [...existing, ...slots]);
            });
        }
        return map;
    }, [availability]);

    const reservedTimesByDateMap = useMemo(() => {
        const map = new Map<string, Set<string>>();
        if (availability) {
            availability.forEach((day) => {
                const key = day.date;
                const reservedSet = new Set<string>();

                if (day.hasReservation && day.reservations) {
                    day.reservations.forEach(res => {
                        const timeStr = typeof res.reservationTime === 'string'
                            ? res.reservationTime
                            : (res.reservationTime as any).start || res.reservationTime;

                        const time = String(timeStr).substring(0, 5);
                        reservedSet.add(time);
                    });
                }
                map.set(key, reservedSet);
            });
        }
        return map;
    }, [availability]);

    const isTimeSelectionValid = useCallback((dateToValidate: Dayjs | null, timeToValidate: string): boolean => {
        if (!timeToValidate || !dateToValidate) return false;

        const key = dateToValidate.format('YYYY-MM-DD');

        const reservedTimes = reservedTimesByDateMap.get(key);
        if (reservedTimes && reservedTimes.has(timeToValidate)) {
            return false;
        }

        const userTime = dayjs(timeToValidate, 'HH:mm');
        if (!userTime.isValid()) return false;

        const [hh, mm] = timeToValidate.split(':').map(Number);
        const candidate = dateToValidate.hour(hh).minute(mm).second(0);
        if (dateToValidate.isSame(dayjs(), 'day') && candidate.isBefore(dayjs())) {
            return false;
        }

        const availableSlots = availabilityByDateMap.get(key);
        if (!availableSlots?.length) return false;

        return availableSlots.some(slot =>
            userTime.isSameOrAfter(dayjs(slot.start, 'HH:mm')) && userTime.isBefore(dayjs(slot.end, 'HH:mm'))
        );
    }, [availabilityByDateMap, reservedTimesByDateMap]);

    const handleDateChange = useCallback((newDate: Dayjs | null) => {
        setSelectedDate(prev => {
            if (prev && newDate && prev.isSame(newDate, 'day')) {
                return null;
            }
            return newDate;
        });
        setSelectedTime('');
        setTimeError('');
    }, []);

    const handleTimeChange = useCallback((time: string) => {
        setSelectedTime(time);
        if (timeError) setTimeError('');
    }, [timeError]);

    const validateTimeOnBlur = useCallback(() => {
        if (!currentTime || !currentDate) {
            setTimeError('');
            return;
        }
        const key = currentDate.format('YYYY-MM-DD');
        const reservedTimes = reservedTimesByDateMap.get(key);

        if (reservedTimes && reservedTimes.has(currentTime)) {
            setTimeError('Esta hora ya está reservada.');
            return;
        }

        const isValid = isTimeSelectionValid(currentDate, currentTime);
        setTimeError(isValid ? '' : 'La hora seleccionada no es válida.');
    }, [currentDate, currentTime, isTimeSelectionValid, reservedTimesByDateMap]);

    const handleBookNow = useCallback((sessionType?: 'initial' | 'followup' | null) => {
        if (!professionalUid || !currentDate || !currentTime) return;

        const key = currentDate.format('YYYY-MM-DD');
        const reservedTimes = reservedTimesByDateMap.get(key);

        if (reservedTimes && reservedTimes.has(currentTime)) {
            setTimeError('Esta hora ya está reservada.');
            return;
        }

        if (timeError) return;

        if (modality === 'Presencial/Virtual' && !bookingModality) {
            setModalityTooltipOpen(true);
            return;
        }

        const [hours, minutes] = currentTime.split(':').map(Number);
        const bookingDateTime = currentDate.hour(hours).minute(minutes).second(0);
        const formattedDateTime = bookingDateTime.format('YYYY-MM-DD HH:mm:ss');

        let finalType = '';
        if (modality === 'Presencial') finalType = 'Presencial';
        else if (modality === 'Virtual') finalType = 'Virtual';
        else finalType = bookingModality;

        const bookingData: IBookingRequest & { session_type?: string } = {
            professional_user_uid: professionalUid,
            date_booking_start: formattedDateTime,
            notes: "",
            type: finalType,
            session_type: sessionType || undefined
        };

        const currentBookingDetails = localStorageHelper.getItem<any>('bookingDetails') || {};
        localStorageHelper.setItem('bookingDetails', {
            ...currentBookingDetails,
            ...bookingData,
            consult_type: finalType
        });

        return true;
    }, [professionalUid, currentDate, currentTime, timeError, reservedTimesByDateMap, modality, bookingModality]);

    const shouldDisableDate = useCallback((date: Dayjs) => {
        if (date.isBefore(dayjs(), 'day')) return true;
        
        if (bookingMode === 'request') {
            if (!requestSchedule) return false;

            const dayOfWeek = date.day(); // 0 is Sunday, 1 to 6 are Monday to Saturday

            if (requestSchedule.type === 'block') {
                if (!requestSchedule.days || requestSchedule.days.length === 0) return false;
                return !requestSchedule.days.includes(dayOfWeek);
            } else if (requestSchedule.type === 'day') {
                if (!requestSchedule.schedule) return false;
                const dayConfig = requestSchedule.schedule[dayOfWeek];
                if (!dayConfig || !dayConfig.active) return true; // Disable if not active or not configured
                return false;
            }

            return false;
        }

        const key = date.format('YYYY-MM-DD');
        const slots = availabilityByDateMap.get(key) || [];
        
        if (slots.length === 0) return true;

        return false;
    }, [availabilityByDateMap, bookingMode, requestSchedule]);

    const timeOptions = useMemo((): TimeOption[] => {
        if (!currentDate) return [];
        const key = currentDate.format('YYYY-MM-DD');
        const slots = availabilityByDateMap.get(key) || [];
        if (!slots.length) return [];

        const reservedTimes = reservedTimesByDateMap.get(key);

        const stepMinutes = consultTime > 0 ? consultTime : 30;
        const pauseMinutes = bookingMode === 'direct' && availability && availability.length > 0 ? (availability[0].pauseMinutes || 0) : 0;

        const optionsMap = new Map<string, TimeOption>();
        const now = dayjs();

        slots.forEach(({ start, end }) => {
            let cursor = dayjs(start, 'HH:mm');
            const endTime = dayjs(end, 'HH:mm');

            let cursorDateTime = currentDate.hour(cursor.hour()).minute(cursor.minute()).second(0);
            const endDateTime = currentDate.hour(endTime.hour()).minute(endTime.minute()).second(0);

            while (cursorDateTime.isBefore(endDateTime)) {

                const nextSlotTime = cursorDateTime.add(stepMinutes, 'minute');
                if (nextSlotTime.isAfter(endDateTime)) {
                    break;
                }

                const timeString = cursorDateTime.format('HH:mm');

                const isPast = currentDate.isSame(dayjs(), 'day') && cursorDateTime.isBefore(now);

                if (!isPast) {
                    const isReserved = reservedTimes ? reservedTimes.has(timeString) : false;

                    optionsMap.set(timeString, {
                        value: timeString,
                        disabled: isReserved
                    });
                }

                // Advance the cursor to the end of the slot + pauseMinutes
                cursorDateTime = nextSlotTime.add(pauseMinutes, 'minute');
            }
        });

        return Array.from(optionsMap.values()).sort((a, b) => a.value.localeCompare(b.value));

    }, [availabilityByDateMap, reservedTimesByDateMap, currentDate, consultTime, availability]);

    const isBookingDisabled = !currentDate || !currentTime || !!timeError;

    const validateModality = useCallback((): boolean => {
        if (modality === 'Presencial/Virtual' && !bookingModality) {
            setModalityTooltipOpen(true);
            return false;
        }
        return true;
    }, [modality, bookingModality]);

    const saveBookingModality = useCallback(() => {
        let finalType = '';
        if (modality === 'Presencial') finalType = 'Presencial';
        else if (modality === 'Virtual') finalType = 'Virtual';
        else finalType = bookingModality;

        const currentBookingDetails = localStorageHelper.getItem<any>('bookingDetails') || {};
        localStorageHelper.setItem('bookingDetails', {
            ...currentBookingDetails,
            consult_type: finalType
        });
    }, [modality, bookingModality]);

    return {
        selectedDate,
        selectedTime,
        timeError,
        isBookingDisabled,
        availabilityMap: availabilityByDateMap,
        timeOptions,
        handleDateChange,
        handleTimeChange,
        validateTimeOnBlur,
        handleBookNow,
        shouldDisableDate,
        isTimeSelectionValid,
        bookingModality,
        setBookingModality,
        modalityTooltipOpen,
        setModalityTooltipOpen,
        validateModality,
        saveBookingModality
    };
};